import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Phone, ShieldCheck, ShieldOff, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useAuth } from '@/app/AuthProvider';
import { requiresMfa } from '@/lib/permissions';
import { formatPhone, formatRelative } from '@/lib/formatters';
import { useUnenrollFactor } from '@/features/auth/mfa';

/**
 * Per-user "Security" page reachable from the Topbar avatar dropdown.
 * Only deals with MFA today; if we ever add session management, recovery
 * codes, or trusted devices they should land here too.
 */
export function MfaSettingsPage() {
  const { profile, mfa, refreshMfa } = useAuth();
  const unenroll = useUnenrollFactor();
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const phoneFactors = mfa.factors.filter((f) => f.factor_type === 'phone');
  const required = requiresMfa(profile?.role ?? null);

  const onRemove = async (factorId: string) => {
    try {
      await unenroll.mutateAsync({ factorId });
      toast.success('Factor removed.');
      setConfirmRemove(null);
      await refreshMfa();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not remove factor.';
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Security"
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-old-growth" /> 2-step verification
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>Current session:</span>
            {mfa.currentLevel === 'aal2' ? (
              <Badge variant="success">AAL2 — verified</Badge>
            ) : (
              <Badge variant="muted">AAL1 — password / SSO only</Badge>
            )}
            {required && mfa.currentLevel !== 'aal2' && (
              <Badge variant="default">2-step required</Badge>
            )}
          </div>

          {phoneFactors.length === 0 ? (
            <EmptyState
              icon={Phone}
              title="No phone enrolled"
              action={
                <Button asChild>
                  <Link to="/auth/mfa/setup">Add phone</Link>
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {phoneFactors.map((f) => (
                <li key={f.id} className="flex items-center gap-3 p-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {f.friendly_name
                        ? formatPhone(f.friendly_name.replace(/\s*\([^)]*\)$/, ''))
                        : 'Phone factor'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Added {formatRelative(f.created_at)}
                    </p>
                  </div>
                  {f.status === 'verified' ? (
                    <Badge variant="success">Verified</Badge>
                  ) : (
                    <Badge variant="muted">Unverified</Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmRemove(f.id)}
                    disabled={unenroll.isPending}
                    aria-label="Remove factor"
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {phoneFactors.some((f) => f.status === 'verified') && (
            <Button asChild variant="outline">
              <Link to="/auth/mfa/setup">Replace phone</Link>
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldOff className="h-4 w-4 text-destructive" /> Remove factor?
            </DialogTitle>
            <DialogDescription>
              {required
                ? 'Your role requires 2-step verification. If you remove your only phone factor you will be signed out and forced to re-enroll on your next sign-in. Atlas will not let you view financial data until a new factor is verified.'
                : 'You can re-add a phone any time. Without a verified factor your sessions will sign in at AAL1 only.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmRemove(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmRemove && onRemove(confirmRemove)}
              disabled={unenroll.isPending}
            >
              {unenroll.isPending ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
